/**
 * A browser's connection to the API, shared by the dashboard and the widget.
 *
 * Both need the same three things, and the subtle failures live in all three, so
 * one implementation is worth more than two convenient ones: cookie sessions, a
 * CSRF value echoed from a cookie into a header, and a refresh that must never run
 * twice at once.
 *
 * Sessions are carried in `httpOnly` cookies, so this file never sees, stores, or
 * transmits a token. That is the whole reason both clients use cookies rather than
 * bearer auth: an XSS bug in a page that renders customer-supplied text would
 * otherwise hand over the session, and in the dashboard's case that session can read
 * every conversation in the platform.
 *
 * What it does hold is the CSRF value, which is readable by design — a cross-origin
 * attacker can make the browser send cookies but cannot read one to echo it back.
 *
 * **The widget is cross-origin and that is fine.** It runs on
 * `shopislandpeptide.com` and calls `api.shopislandpeptide.com`. Those are different
 * origins but the *same site*, since same-site is decided by registrable domain, so
 * `SameSite=Lax` cookies are still sent. `credentials: "include"` remains mandatory
 * on every call regardless.
 */
export class ApiError extends Error {
    status;
    code;
    name = "ApiError";
    constructor(message, status, 
    /** Stable machine-readable code from the API, when it sent one. */
    code) {
        super(message);
        this.status = status;
        this.code = code;
    }
}
/** Raised when the session is gone and the caller should be sent to the login page. */
export class SessionExpiredError extends Error {
    name = "SessionExpiredError";
}
/**
 * Names duplicated from the API rather than imported.
 *
 * The package that owns them is a Zod-based server package, and importing it here
 * would put a validation library into a bundle that loads on every product page for
 * no benefit. The same trade was already made for the CSRF header below; these
 * strings are part of a wire contract and change about as often as the wire does.
 */
const CSRF_COOKIE = {
    staff: "island_csrf",
    customer: "island_ccsrf",
};
const CSRF_HEADER = "x-island-csrf";
const AUDIENCE_HEADER = "x-island-audience";
function csrfToken(audience) {
    for (const part of document.cookie.split(";")) {
        const separator = part.indexOf("=");
        if (separator === -1)
            continue;
        if (part.slice(0, separator).trim() === CSRF_COOKIE[audience]) {
            return decodeURIComponent(part.slice(separator + 1).trim());
        }
    }
    return null;
}
export function createApiClient(options) {
    /**
     * A single in-flight refresh, shared by every caller.
     *
     * Access tokens last fifteen minutes, so a dashboard left open overnight will have
     * several requests fail at once when it wakes. Without this, each would fire its
     * own refresh — and because every refresh rotates, the second would present a
     * token the first had already spent, which the platform correctly treats as theft
     * and responds to by revoking the whole session. One shared promise is what stops
     * the dashboard logging itself out.
     */
    let refreshInFlight = null;
    async function refreshSession() {
        refreshInFlight ??= (async () => {
            try {
                const response = await fetch(`${options.baseUrl}/auth/refresh`, {
                    method: "POST",
                    credentials: "include",
                    // No content-type, because there is no body. Declaring JSON and then
                    // sending nothing makes the server reject the request as malformed, and a
                    // refresh that always fails logs the agent out every fifteen minutes.
                    // The refresh token travels in a cookie; the request carries nothing else.
                    //
                    // The audience matters here more than anywhere: a browser holding both
                    // sessions has two refresh cookies, and refreshing the wrong one would
                    // rotate a session this client does not own.
                    headers: { accept: "application/json", [AUDIENCE_HEADER]: options.audience },
                });
                return response.ok;
            }
            catch {
                return false;
            }
            finally {
                // Cleared on the next tick so concurrent callers all observe this attempt
                // rather than starting another.
                queueMicrotask(() => {
                    refreshInFlight = null;
                });
            }
        })();
        return await refreshInFlight;
    }
    async function request(path, init = {}) {
        const method = init.method ?? "GET";
        const csrf = csrfToken(options.audience);
        const response = await fetch(`${options.baseUrl}${path}`, {
            method,
            // Required on every call, or the cookies are neither sent nor set.
            credentials: "include",
            headers: {
                accept: "application/json",
                [AUDIENCE_HEADER]: options.audience,
                ...(init.binary === undefined
                    ? init.body === undefined
                        ? {}
                        : { "content-type": "application/json" }
                    : { "content-type": init.binary.contentType }),
                // Only state-changing requests are checked, but sending it always is
                // harmless and removes a decision from every call site.
                ...(csrf === null ? {} : { [CSRF_HEADER]: csrf }),
            },
            ...(init.binary === undefined
                ? init.body === undefined
                    ? {}
                    : { body: JSON.stringify(init.body) }
                : { body: init.binary.bytes }),
        });
        if (response.status === 401 && init.noRetry !== true) {
            // The access token has probably just expired. Refresh once and retry; if that
            // fails the session is genuinely gone.
            if (await refreshSession()) {
                return await request(path, { ...init, noRetry: true });
            }
            options.onSessionLost();
            throw new SessionExpiredError("The session has expired.");
        }
        if (!response.ok) {
            const body = (await response.json().catch(() => null));
            throw new ApiError(body?.error?.message ?? `Request failed with ${String(response.status)}`, response.status, body?.error?.code);
        }
        if (response.status === 204)
            return undefined;
        return (await response.json());
    }
    return {
        get: async (path) => await request(path),
        post: async (path, body) => await request(path, { method: "POST", ...(body === undefined ? {} : { body }) }),
        // `put` for whole-resource replacement — the assistant's configuration is saved
        // in full rather than patched, so a field removed in the editor is a field
        // removed on the server rather than one silently retaining its old value.
        put: async (path, body) => await request(path, { method: "PUT", ...(body === undefined ? {} : { body }) }),
        patch: async (path, body) => await request(path, { method: "PATCH", ...(body === undefined ? {} : { body }) }),
        /** Send a file as the body. See `RequestOptions.binary`. */
        putBinary: async (path, bytes, contentType) => await request(path, { method: "PUT", binary: { bytes, contentType } }),
        delete: async (path, body) => await request(path, { method: "DELETE", ...(body === undefined ? {} : { body }) }),
        refreshSession,
    };
}
//# sourceMappingURL=session-client.js.map